import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CreditCard, ShieldCheck, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { trackInitiateCheckout } from "@/lib/analytics";

const REASON_PRESETS = [
  "Fake / never-visited (policy)",
  "Off-topic / no service received",
  "Conflict of interest (ex-employee)",
  "Suspected spam / duplicate account",
  "Harassment / hate speech",
  "Personal information disclosure",
  "Other (see review URL)",
];

/**
 * Guest checkout dialog for a single $195 review removal.
 * No account required — the backend auto-provisions a lightweight guest user.
 */
export default function RemovalCheckoutDialog({ open, onClose }) {
  const [form, setForm] = useState({
    name: "",
    business_name: "",
    email: "",
    phone: "",
    profile: "",
    review_snippet: "",
    review_url: "",
    reason: REASON_PRESETS[0],
  });
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault?.();
    const errors = [];
    if (!form.name.trim()) errors.push("Full name");
    if (!form.business_name.trim()) errors.push("Business name");
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errors.push("A valid email");
    if (!form.review_snippet.trim() || form.review_snippet.trim().length < 5)
      errors.push("The review text (5+ chars)");
    if (errors.length) {
      toast.error(`Please fill in: ${errors.join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      trackInitiateCheckout("review_removal", 195);
      const { data } = await api.post("/payments/removal-checkout", {
        origin_url: window.location.origin,
        name: form.name.trim(),
        business_name: form.business_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        profile: form.profile.trim() || undefined,
        review_snippet: form.review_snippet.trim(),
        review_url: form.review_url.trim() || undefined,
        reason: form.reason,
      });
      window.location.href = data.url;
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Couldn't start your checkout — please try again in a moment.";
      toast.error(typeof msg === "string" ? msg : "Checkout failed.");
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/75 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
      data-testid="removal-dialog"
    >
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d1424] p-6 sm:p-8 relative my-8">
        {!loading && (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="removal-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#10B981] font-bold">
          <ShieldCheck className="w-3.5 h-3.5" />
          Report a review · $195
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold mt-2 leading-tight">
          Submit a review for removal
        </h2>
        <p className="mt-2 text-white/55 text-sm leading-relaxed">
          Tell us about the review and we'll take you to secure Stripe checkout.
          No account needed — you'll get a receipt and access to track progress.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your full name" required>
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                autoComplete="name"
                placeholder="Jane Rousseau"
                data-testid="removal-name"
                disabled={loading}
                className={inputCls}
              />
            </Field>
            <Field label="Business name" required>
              <input
                type="text"
                value={form.business_name}
                onChange={set("business_name")}
                autoComplete="organization"
                placeholder="Riverside Bistro"
                data-testid="removal-business"
                disabled={loading}
                className={inputCls}
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Email for receipt & updates" required>
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                autoComplete="email"
                placeholder="you@business.com"
                data-testid="removal-email"
                disabled={loading}
                className={inputCls}
              />
            </Field>
            <Field label="Phone / WhatsApp (optional)">
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                autoComplete="tel"
                placeholder="+92 333 000 0000"
                data-testid="removal-phone"
                disabled={loading}
                className={inputCls}
              />
            </Field>
          </div>

          <Field label="Google Business URL (optional)">
            <input
              type="url"
              value={form.profile}
              onChange={set("profile")}
              placeholder="https://maps.google.com/…"
              data-testid="removal-profile"
              disabled={loading}
              className={inputCls}
            />
          </Field>

          <Field label="The review text" required>
            <textarea
              rows={3}
              value={form.review_snippet}
              onChange={set("review_snippet")}
              placeholder='Paste the exact review text — e.g. "Terrible food, worst service ever…"'
              data-testid="removal-snippet"
              disabled={loading}
              className={`${inputCls} h-auto py-2.5 resize-none`}
            />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Direct review URL (optional)">
              <input
                type="url"
                value={form.review_url}
                onChange={set("review_url")}
                placeholder="https://maps.google.com/…"
                data-testid="removal-url"
                disabled={loading}
                className={inputCls}
              />
            </Field>
            <Field label="Why should Google remove it?">
              <select
                value={form.reason}
                onChange={set("reason")}
                data-testid="removal-reason"
                disabled={loading}
                className={inputCls}
              >
                {REASON_PRESETS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="pt-3 flex flex-col-reverse sm:flex-row sm:justify-end sm:items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="text-white/55 hover:text-white text-sm font-semibold h-11 px-4 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              data-testid="removal-submit"
              className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-full bg-[#10B981] hover:bg-[#0ea774] text-white font-semibold text-sm shadow-lg shadow-emerald-500/25 transition-colors disabled:opacity-70"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Pay $195 & submit case
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>

          <p className="text-[11px] text-white/40 leading-relaxed pt-1">
            Guarantee: if Google doesn't remove the review, you get a full refund.
            Payments processed by Stripe — your card details never touch our servers.
          </p>
        </form>
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-11 px-3.5 rounded-lg border border-white/10 bg-white/[0.03] text-white text-sm placeholder:text-white/30 focus:border-[#10B981]/60 outline-none transition-colors";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-[0.14em] text-white/50 font-semibold mb-1.5">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </div>
      {children}
    </label>
  );
}
